<template>
  <div v-if="show" class="travel-time-filter-panel">
    <!-- Loading Overlay - only show when building is selected and loading -->
    <div v-if="loading && localFilters.buildingId" class="loading-overlay">
      <div class="loading-content">
        <svg class="loading-spinner" viewBox="0 0 24 24">
          <circle class="spinner-circle" cx="12" cy="12" r="10" fill="none" stroke-width="3"/>
        </svg>
        <span class="loading-text">Loading travel time data...</span>
      </div>
    </div>
    
    <!-- Header -->
    <div class="filter-panel-header">
      <h4 class="filter-panel-title">Travel Time</h4>
      <div class="header-actions">
        <ThemedButton variant="ghost" size="icon" @click="$emit('close')" aria-label="Close filters">
          <ThemeIcon icon="x" type="icons" classes="w-5 h-5" />
        </ThemedButton>
      </div>
    </div>
    
    <!-- Search Mode Selection -->
    <div class="filter-group">
      <label class="filter-label">Search By</label>
      <div class="search-mode-toggles">
        <label class="radio-option">
          <input 
            type="radio" 
            value="building" 
            v-model="localFilters.searchMode" 
            @change="onSearchModeChange"
          />
          <span>Building</span>
        </label>
        <label class="radio-option">
          <input 
            type="radio" 
            value="location" 
            v-model="localFilters.searchMode" 
            @change="onSearchModeChange"
          />
          <span>Location</span>
        </label>
      </div>
    </div>
    
    <!-- Zoom Indicator (shown when no building/location selected) -->
    <!-- <div v-if="showZoomIndicator" class="zoom-indicator">
      <ThemeIcon icon="info-icon" type="icons" classes="w-5 h-5" />
      <span>{{ zoomIndicatorMessage }}</span>
    </div> -->
    
    <!-- Travel Mode Selection -->
    <div class="filter-group">
      <label class="filter-label">Travel Mode</label>
      <div class="travel-mode-buttons">
        <ThemedButton
          variant="outline"
          size="icon"
          @click="selectTravelMode('walking')" 
          :class="{ active: localFilters.travelMode === 'walking' }"
          class="travel-mode-button"
          :aria-label="'Walking mode'"
          title="Walking"
        >
          <ThemeIcon icon="walking-person" type="icons" />
        </ThemedButton>
        <ThemedButton
          variant="outline"
          size="icon"
          @click="selectTravelMode('driving')" 
          :class="{ active: localFilters.travelMode === 'driving' }"
          class="travel-mode-button"
          :aria-label="'Driving mode'"
          title="Driving"
        >
          <ThemeIcon icon="car" type="icons" />
        </ThemedButton>
        <ThemedButton
          variant="outline"
          size="icon"
          @click="selectTravelMode('public_transport')" 
          :class="{ active: localFilters.travelMode === 'public_transport' }"
          class="travel-mode-button"
          :aria-label="'Public transport mode'"
          title="Public Transport"
        >
          <ThemeIcon icon="transit-icon" type="icons" />
        </ThemedButton>
        <ThemedButton
          variant="outline"
          size="icon"
          @click="selectTravelMode('cycling')" 
          :class="{ active: localFilters.travelMode === 'cycling' }"
          class="travel-mode-button"
          :aria-label="'Cycling mode'"
          title="Cycling"
        >
          <ThemeIcon icon="bicycle" type="icons" />
        </ThemedButton>
      </div>
    </div>
    
    <!-- Time and Building/Location Selection -->
    <div class="filter-group">
      <label class="filter-label">Within</label>
      <div class="time-search-row">
        <ThemedSelect
          v-model="localFilters.travelTime" 
          :options="travelTimeOptions"
          @input="emitFilters"
        />
        <span class="of-label">of</span>
        
        <!-- Building Dropdown (shown when searchMode is 'building') -->
        <ThemedSelect
          v-if="localFilters.searchMode === 'building'"
          v-model="localFilters.buildingId" 
          :options="buildingOptions"
          :key="`building-select-${buildings.length}`"
          placeholder="Select Building"
          @input="emitFilters"
        />
        
        <!-- Location Search Input (shown when searchMode is 'location') -->
        <LocationSearch
          v-if="localFilters.searchMode === 'location'"
          :initial-value="localFilters.locationQuery"
          @location-selected="onLocationSelected"
        />
      </div>
    </div>
    
    <!-- Arrive/Depart Options -->
    <div class="filter-group">
      <div class="arrive-depart-row">
        <ThemedSelect
          v-model="localFilters.arriveOrDepart" 
          :options="arriveOrDepartOptions"
          @input="emitFilters"
        />
        <ThemedSelect
          v-model="localFilters.arriveOrDepartTime" 
          :options="timeOptions"
          @input="emitFilters"
        />
      </div>
    </div>
    
    <!-- Traffic Layer Toggle -->
    <div class="filter-group">
      <div class="layer-toggle-row">
        <div class="layer-toggle-info">
          <ThemeIcon icon="lightning" type="icons" classes="w-5 h-5 layer-icon" />
          <div class="layer-toggle-text">
            <span class="layer-toggle-label">Traffic Layer</span>
            <span class="layer-toggle-description">Show live traffic conditions</span>
          </div>
        </div>
        <ThemedToggle
          :value="isTrafficLayerEnabled"
          @input="toggleTrafficLayer"
          aria-label="Toggle traffic layer"
        />
      </div>
    </div>
    
    <!-- Traffic Condition Slider -->
    <div class="filter-group">
      <label class="filter-label">Traffic Condition</label>
      <div class="traffic-condition-slider">
        <div class="slider-labels">
          <span class="slider-label-start">Light</span>
          <span class="slider-label-end">Heavy</span>
        </div>
        <input 
          type="range" 
          min="0.5" 
          max="1.0" 
          step="0.05" 
          v-model.number="localFilters.trafficMultiplier"
          @input="onTrafficSliderInput"
          class="traffic-slider"
        />
        <div class="slider-value-display">
          <span class="value-label">Multiplier:</span>
          <span class="value-number">{{ localFilters.trafficMultiplier.toFixed(1) }}x</span>
        </div>
        <p class="slider-description">
          Adjust for traffic conditions. Light traffic (left) = larger area, Heavy traffic (right) = smaller area.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import ThemeIcon from '../ThemeIcon.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedToggle from '../ThemedToggle.vue';
import LocationSearch from './LocationSearch.vue';

export default {
  name: 'TravelTimeFilterPanel',
  
  components: {
    ThemeIcon,
    ThemedSelect,
    ThemedButton,
    ThemedToggle,
    LocationSearch
  },
  
  props: {
    filters: {
      type: Object,
      required: true,
      default: () => ({
        searchMode: 'building',
        travelMode: 'driving',
        travelTime: 1800,
        buildingId: null,
        locationQuery: '',
        locationCoordinates: null,
        arriveOrDepart: 'depart',
        arriveOrDepartTime: '7',
        trafficMultiplier: 0.65
      })
    },
    show: {
      type: Boolean,
      default: false
    },
    buildings: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: true
    },
    activeLayers: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      localFilters: {
        searchMode: 'building',
        travelMode: 'driving',
        travelTime: 1800,
        buildingId: null,
        locationQuery: '',
        locationCoordinates: null,
        arriveOrDepart: 'depart',
        arriveOrDepartTime: '7',
        trafficMultiplier: 0.65 // Default traffic condition (realistic/normal)
      },
      trafficSliderDebounceTimer: null // Timer for debouncing slider input
    };
  },
  
  computed: {
    /**
     * Show extended time options for driving and public transport
     */
    showExtendedTimes() {
      return ['driving', 'public_transport'].includes(this.localFilters.travelMode);
    },
    
    /**
     * Check if traffic layer is enabled
     */
    isTrafficLayerEnabled() {
      return this.activeLayers.includes('traffic');
    },
    
    /**
     * Show zoom indicator when no building or location is selected
     */
    showZoomIndicator() {
      if (this.localFilters.searchMode === 'building') {
        return !this.localFilters.buildingId;
      } else if (this.localFilters.searchMode === 'location') {
        return !this.localFilters.locationCoordinates;
      }
      return true;
    },
    
    /**
     * Zoom indicator message based on search mode
     */
    zoomIndicatorMessage() {
      if (this.localFilters.searchMode === 'building') {
        return 'Select a building to view travel time zones';
      } else if (this.localFilters.searchMode === 'location') {
        return 'Search for a location to view travel time zones';
      }
      return 'Select a search option';
    },
    
    travelTimeOptions() {
      const baseOptions = [
        { label: '15 min', value: '900' },
        { label: '30 min', value: '1800' }
      ];
      
      if (this.showExtendedTimes) {
        return [
          ...baseOptions,
          { label: '1 hr', value: '3600' },
          { label: '1.5 hr', value: '5400' },
          { label: '2 hr', value: '7200' }
        ];
      }
      
      return baseOptions;
    },
    
    buildingOptions() {
      return this.buildings.map(building => ({
        label: this.formatBuildingAddress(building),
        value: String(building.id) // Convert to string for select compatibility
      }));
    },
    
    arriveOrDepartOptions() {
      return [
        { label: 'Depart', value: 'depart' },
        { label: 'Arrive', value: 'arrive' }
      ];
    },
    
    timeOptions() {
      return [
        { label: 'Now', value: '' },
        { label: '5:00 AM', value: '5' },
        { label: '6:00 AM', value: '6' },
        { label: '7:00 AM', value: '7' },
        { label: '8:00 AM', value: '8' },
        { label: '9:00 AM', value: '9' },
        { label: '10:00 AM', value: '10' },
        { label: '11:00 AM', value: '11' },
        { label: '12:00 PM', value: '12' },
        { label: '1:00 PM', value: '13' },
        { label: '2:00 PM', value: '14' },
        { label: '3:00 PM', value: '15' },
        { label: '4:00 PM', value: '16' },
        { label: '5:00 PM', value: '17' },
        { label: '6:00 PM', value: '18' },
        { label: '7:00 PM', value: '19' },
        { label: '8:00 PM', value: '20' },
        { label: '9:00 PM', value: '21' },
        { label: '10:00 PM', value: '22' },
        { label: '11:00 PM', value: '23' },
        { label: '12:00 AM', value: '24' }
      ];
    }
  },
  
  watch: {
    filters: {
      handler(newFilters) {
        // Invert the trafficMultiplier back for display
        // Parent sends: 0.5-1.0 (actual multiplier)
        // Slider needs: 1.0-0.5 (inverted for UX)
        const displayMultiplier = newFilters.trafficMultiplier 
          ? 1.5 - newFilters.trafficMultiplier 
          : 0.65;
        
        this.localFilters = {
          searchMode: newFilters.searchMode || 'building',
          travelMode: newFilters.travelMode || 'driving',
          travelTime: newFilters.travelTime || 1800,
          // Convert buildingId to string for select compatibility
          buildingId: newFilters.buildingId ? String(newFilters.buildingId) : null,
          locationQuery: newFilters.locationQuery || '',
          locationCoordinates: newFilters.locationCoordinates || null,
          arriveOrDepart: newFilters.arriveOrDepart || 'depart',
          arriveOrDepartTime: newFilters.arriveOrDepartTime || '7',
          trafficMultiplier: displayMultiplier
        };
      },
      deep: true,
      immediate: true
    }
  },
  
  beforeDestroy() {
    // Clean up debounce timer
    if (this.trafficSliderDebounceTimer) {
      clearTimeout(this.trafficSliderDebounceTimer);
    }
  },
  
  methods: {
    /**
     * Handle location selection from LocationSearch component
     */
    onLocationSelected(location) {
      console.log('[TravelTime] Location selected:', location);
      
      // Update local filters with location data
      this.localFilters.locationQuery = location.address || location.name;
      this.localFilters.locationCoordinates = {
        lat: location.lat,
        lng: location.lng
      };
      
      // Emit updated filters to parent
      this.emitFilters();
    },
    
    /**
     * Select travel mode and emit filters
     */
    selectTravelMode(mode) {
      this.localFilters.travelMode = mode;
      
      // Reset travel time to 30 min if extended time was selected for walking/cycling
      if (!this.showExtendedTimes && this.localFilters.travelTime > 1800) {
        this.localFilters.travelTime = 1800;
      }
      
      this.emitFilters();
    },
    
    /**
     * Handle search mode change
     */
    onSearchModeChange() {
      // Clear opposite mode's data when switching
      if (this.localFilters.searchMode === 'building') {
        this.localFilters.locationQuery = '';
        this.localFilters.locationCoordinates = null;
      } else if (this.localFilters.searchMode === 'location') {
        this.localFilters.buildingId = null;
      }
      
      this.emitFilters();
    },
    
    /**
     * Emit filter changes to parent component
     */
    emitFilters() {
      // Convert buildingId back to number for API compatibility
      const filters = { ...this.localFilters };
      if (filters.buildingId) {
        filters.buildingId = parseInt(filters.buildingId, 10);
      }
      
      // Invert the traffic multiplier for intuitive UX
      // Slider shows: 0.5 (Heavy) to 1.0 (Light)
      // But we emit: 1.0 (Heavy) to 0.5 (Light) for the formula
      // Inversion: emittedValue = 1.5 - sliderValue
      filters.trafficMultiplier = 1.5 - this.localFilters.trafficMultiplier;
      
      this.$emit('update:filters', filters);
    },
    
    /**
     * Format building address for dropdown display
     */
    formatBuildingAddress(building) {
      if (!building) return '';
      
      const parts = [];
      if (building.address) {
        parts.push(building.address);
      }
      if (building.city) {
        parts.push(building.city);
      }
      if (building.state) {
        parts.push(building.state);
      }
      
      return parts.join(', ');
    },
    
    /**
     * Toggle traffic layer on/off
     */
    toggleTrafficLayer() {
      this.$emit('layer-toggled', {
        layerId: 'traffic',
        enabled: !this.isTrafficLayerEnabled
      });
    },
    
    /**
     * Handle traffic slider input with debouncing
     * Debounces API calls to prevent too many requests while sliding
     */
    onTrafficSliderInput() {
      // Clear existing timer
      if (this.trafficSliderDebounceTimer) {
        clearTimeout(this.trafficSliderDebounceTimer);
      }
      
      // Set new timer to emit filters after 500ms of no input
      this.trafficSliderDebounceTimer = setTimeout(() => {
        this.emitFilters();
      }, 500);
    }
  }
};
</script>
